import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
    Check, 
    X, 
    Clock, 
    User,
    Mail,
    Phone,
    MapPin,
    Briefcase,
    MessageSquare,
    Heart,
    Sparkles,
    FileText,
    Trash2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ApplicationsPage = () => {
    const [applications, setApplications] = useState([]);
    const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0 });
    const [loading, setLoading] = useState(true);
    const [selectedApp, setSelectedApp] = useState(null);
    const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
    const [adminNotes, setAdminNotes] = useState('');
    const [activeTab, setActiveTab] = useState('pending');

    useEffect(() => {
        fetchApplications();
        fetchStats();
    }, []);

    const fetchApplications = async () => {
        try {
            const response = await axios.get(`${API}/applications`);
            setApplications(response.data);
        } catch (error) {
            toast.error('Failed to load applications');
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const response = await axios.get(`${API}/applications/stats/summary`);
            setStats(response.data);
        } catch (error) {
            console.error('Failed to load stats');
        }
    };

    const handleReview = async (status) => {
        if (!selectedApp) return;
        
        try {
            await axios.put(`${API}/applications/${selectedApp.id}/review`, {
                status,
                admin_notes: adminNotes
            });
            toast.success(`Application ${status === 'approved' ? 'approved' : 'rejected'} successfully`);
            setIsReviewModalOpen(false);
            setSelectedApp(null);
            setAdminNotes('');
            setActiveTab(status); // Switch to the appropriate tab after review
            fetchApplications();
            fetchStats();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to review application');
        }
    };

    const handleDelete = async (appId) => {
        if (!window.confirm('Are you sure you want to delete this application?')) return;
        
        try {
            await axios.delete(`${API}/applications/${appId}`);
            toast.success('Application deleted');
            fetchApplications();
            fetchStats();
        } catch (error) {
            toast.error('Failed to delete application');
        }
    };

    const openReviewModal = (app) => {
        setSelectedApp(app);
        setAdminNotes(app.admin_notes || '');
        setIsReviewModalOpen(true);
    };

    const getFilteredApplications = (status) => {
        return applications.filter(app => app.status === status);
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'pending':
                return <Badge className="bg-[#D98342] text-white"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
            case 'approved':
                return <Badge className="bg-[#4A6B53] text-white"><Check className="w-3 h-3 mr-1" />Approved</Badge>;
            case 'rejected':
                return <Badge className="bg-[#A64444] text-white"><X className="w-3 h-3 mr-1" />Rejected</Badge>;
            default:
                return null;
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-pulse text-[#A0A0A0]">Loading applications...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="applications-page">
            {/* Header */}
            <div>
                <h1 className="text-4xl font-bold text-white font-['Playfair_Display'] tracking-tight">
                    Applications
                </h1>
                <p className="text-[#A0A0A0] mt-1">
                    Review and manage membership applications
                </p>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <Card className="border-[#5A6350] shadow-sm" data-testid="pending-stat">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-[#A0A0A0] font-medium">Pending Review</p>
                                <p className="text-3xl font-bold text-[#D98342] mt-2 font-['Playfair_Display']">
                                    {stats.pending}
                                </p>
                            </div>
                            <div className="bg-[#D98342] p-3 rounded-xl text-white">
                                <Clock className="w-6 h-6" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="border-[#5A6350] shadow-sm" data-testid="approved-stat">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-[#A0A0A0] font-medium">Approved</p>
                                <p className="text-3xl font-bold text-[#4A6B53] mt-2 font-['Playfair_Display']">
                                    {stats.approved}
                                </p>
                            </div>
                            <div className="bg-[#4A6B53] p-3 rounded-xl text-white">
                                <Check className="w-6 h-6" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="border-[#5A6350] shadow-sm" data-testid="rejected-stat">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-[#A0A0A0] font-medium">Rejected</p>
                                <p className="text-3xl font-bold text-[#A64444] mt-2 font-['Playfair_Display']">
                                    {stats.rejected}
                                </p>
                            </div>
                            <div className="bg-[#A64444] p-3 rounded-xl text-white">
                                <X className="w-6 h-6" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Applications Tabs */}
            <Card className="border-[#5A6350] shadow-sm">
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <CardHeader className="pb-0">
                        <TabsList className="w-full sm:w-auto">
                            <TabsTrigger value="pending" data-testid="tab-pending">
                                Pending ({stats.pending})
                            </TabsTrigger>
                            <TabsTrigger value="approved" data-testid="tab-approved">
                                Approved ({stats.approved})
                            </TabsTrigger>
                            <TabsTrigger value="rejected" data-testid="tab-rejected">
                                Rejected ({stats.rejected})
                            </TabsTrigger>
                        </TabsList>
                    </CardHeader>
                    <CardContent className="pt-6">
                        {['pending', 'approved', 'rejected'].map((status) => (
                            <TabsContent key={status} value={status} className="mt-0">
                                {getFilteredApplications(status).length > 0 ? (
                                    <div className="space-y-4">
                                        {getFilteredApplications(status).map((app) => (
                                            <div 
                                                key={app.id}
                                                className="border border-[#5A6350] rounded-xl p-6 hover:shadow-md transition-shadow"
                                                data-testid={`application-${app.id}`}
                                            >
                                                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-3 mb-3">
                                                            <div className="w-12 h-12 rounded-full bg-[#C9A227] flex items-center justify-center text-white font-semibold text-lg">
                                                                {app.name?.charAt(0).toUpperCase()}
                                                            </div>
                                                            <div>
                                                                <h3 className="font-semibold text-lg text-white">{app.name}</h3>
                                                                <p className="text-sm text-[#A0A0A0]">
                                                                    Applied {new Date(app.submitted_at).toLocaleDateString()}
                                                                </p>
                                                            </div>
                                                            {getStatusBadge(app.status)}
                                                        </div>

                                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                                                            <div className="flex items-center gap-2 text-[#A0A0A0]">
                                                                <Mail className="w-4 h-4" />
                                                                <span>{app.email}</span>
                                                            </div>
                                                            {app.phone && (
                                                                <div className="flex items-center gap-2 text-[#A0A0A0]">
                                                                    <Phone className="w-4 h-4" />
                                                                    <span>{app.phone}</span>
                                                                </div>
                                                            )}
                                                            {app.profession && (
                                                                <div className="flex items-center gap-2 text-[#A0A0A0]">
                                                                    <Briefcase className="w-4 h-4" />
                                                                    <span>{app.profession}</span>
                                                                </div>
                                                            )}
                                                            {app.location && (
                                                                <div className="flex items-center gap-2 text-[#A0A0A0]">
                                                                    <MapPin className="w-4 h-4" />
                                                                    <span>{app.location}</span>
                                                                </div>
                                                            )}
                                                            {app.referral_source && (
                                                                <div className="flex items-center gap-2 text-[#A0A0A0]">
                                                                    <MessageSquare className="w-4 h-4" />
                                                                    <span>{app.referral_source}</span>
                                                                </div>
                                                            )}
                                                        </div>

                                                        {app.interests?.length > 0 && (
                                                            <div className="flex flex-wrap gap-1.5 mt-3">
                                                                {app.interests.map((interest, idx) => (
                                                                    <span
                                                                        key={idx}
                                                                        className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#3E4A3D]/10 text-[#3E4A3D]"
                                                                    >
                                                                        {interest}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}

                                                        {app.why_join && (
                                                            <div className="mt-3 p-3 bg-[#3D4435] rounded-lg">
                                                                <p className="text-sm text-[#A0A0A0] italic">"{app.why_join}"</p>
                                                            </div>
                                                        )}

                                                        {app.admin_notes && (
                                                            <div className="mt-3 p-3 bg-[#C9A227]/5 rounded-lg border border-[#845642]/20">
                                                                <p className="text-xs font-medium text-[#C9A227] mb-1">Admin Notes:</p>
                                                                <p className="text-sm text-[#A0A0A0]">{app.admin_notes}</p>
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="flex lg:flex-col gap-2">
                                                        {app.status === 'pending' ? (
                                                            <>
                                                                <Button
                                                                    onClick={() => openReviewModal(app)}
                                                                    className="bg-[#C9A227] hover:bg-[#B8922A] text-white flex-1 lg:flex-none"
                                                                    data-testid={`review-${app.id}`}
                                                                >
                                                                    <FileText className="w-4 h-4 mr-2" />
                                                                    Review
                                                                </Button>
                                                            </>
                                                        ) : (
                                                            <Button
                                                                variant="outline"
                                                                onClick={() => handleDelete(app.id)}
                                                                className="text-[#A64444] border-[#A64444] hover:bg-[#A64444] hover:text-white"
                                                                data-testid={`delete-${app.id}`}
                                                            >
                                                                <Trash2 className="w-4 h-4 mr-2" />
                                                                Delete
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-12">
                                        <FileText className="w-16 h-16 text-[#E6E1D8] mx-auto mb-4" />
                                        <h3 className="text-lg font-semibold text-white mb-2">
                                            No {status} applications
                                        </h3>
                                        <p className="text-[#A0A0A0]">
                                            {status === 'pending' 
                                                ? 'All applications have been reviewed'
                                                : `No applications have been ${status} yet`
                                            }
                                        </p>
                                    </div>
                                )}
                            </TabsContent>
                        ))}
                    </CardContent>
                </Tabs>
            </Card>

            {/* Review Modal */}
            <Dialog open={isReviewModalOpen} onOpenChange={setIsReviewModalOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="text-2xl font-bold text-white font-['Playfair_Display']">
                            Review Application
                        </DialogTitle>
                    </DialogHeader>
                    {selectedApp && (
                        <div className="space-y-6 mt-4">
                            <div className="flex items-center gap-4">
                                <div className="w-16 h-16 rounded-full bg-[#C9A227] flex items-center justify-center text-white font-bold text-2xl">
                                    {selectedApp.name?.charAt(0).toUpperCase()}
                                </div>
                                <div>
                                    <h3 className="text-xl font-semibold text-white">{selectedApp.name}</h3>
                                    <p className="text-[#A0A0A0]">{selectedApp.email}</p>
                                </div>
                            </div>

                            <div className="space-y-3 text-sm">
                                {selectedApp.profession && (
                                    <div className="flex items-center gap-2">
                                        <Briefcase className="w-4 h-4 text-[#C9A227]" />
                                        <span className="text-[#A0A0A0]">{selectedApp.profession}</span>
                                    </div>
                                )}
                                {selectedApp.location && (
                                    <div className="flex items-center gap-2">
                                        <MapPin className="w-4 h-4 text-[#C9A227]" />
                                        <span className="text-[#A0A0A0]">{selectedApp.location}</span>
                                    </div>
                                )}
                                {selectedApp.referral_source && (
                                    <div className="flex items-center gap-2">
                                        <MessageSquare className="w-4 h-4 text-[#C9A227]" />
                                        <span className="text-[#A0A0A0]">Referred via: {selectedApp.referral_source}</span>
                                    </div>
                                )}
                            </div>

                            {selectedApp.why_join && (
                                <div className="p-4 bg-[#3D4435] rounded-lg">
                                    <p className="text-xs font-medium text-[#C9A227] mb-2">Why they want to join:</p>
                                    <p className="text-sm text-[#A0A0A0]">"{selectedApp.why_join}"</p>
                                </div>
                            )}

                            <div className="space-y-2">
                                <Label htmlFor="admin_notes">Admin Notes (Optional)</Label>
                                <Textarea
                                    id="admin_notes"
                                    value={adminNotes}
                                    onChange={(e) => setAdminNotes(e.target.value)}
                                    placeholder="Add any notes about this application..."
                                    rows={3}
                                    className="border-[#5A6350]"
                                    data-testid="admin-notes-input"
                                />
                            </div>

                            <div className="flex gap-3 pt-4">
                                <Button
                                    onClick={() => handleReview('rejected')}
                                    variant="outline"
                                    className="flex-1 border-[#A64444] text-[#A64444] hover:bg-[#A64444] hover:text-white"
                                    data-testid="reject-btn"
                                >
                                    <X className="w-4 h-4 mr-2" />
                                    Reject
                                </Button>
                                <Button
                                    onClick={() => handleReview('approved')}
                                    className="flex-1 bg-[#4A6B53] hover:bg-[#3E4A3D] text-white"
                                    data-testid="approve-btn"
                                >
                                    <Check className="w-4 h-4 mr-2" />
                                    Approve
                                </Button>
                            </div>
                            <p className="text-xs text-center text-[#A0A0A0]">
                                Approving will automatically create a member profile
                            </p>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ApplicationsPage;
